import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../../App.css';
import './MangaPage.css';
import '../anime/AnimePage.css';
import Header from '../../components/Header';
import { DetailSkeleton } from '../../components/layout/Skeletons';
import Footer from '../../components/Footer';
import PageShell from '../../components/layout/PageShell';
import useScrolled from '../../hooks/useScrolled';
import CustomDropdown from '../../components/controls/CustomDropdown';
import MangaApiService from '../../services/MangaApiService';
import MangaCardComponent from '../../components/mangacard/MangaCardComponent';
import MangaAccessNotice from './MangaAccessNotice';
import { getMangaId, translateStatus, unwrapList, getMangaErrorMessage } from './mangaStudioUtils';
import {
    formatChapterLabel,
    getChapterId,
    readMangaProgress,
    sortChapters,
} from './mangaChapters';
import MangaChapterNavigator from './MangaChapterNavigator';
import MangaReviews from './MangaReviews';
import teamIcon from '../../assets/ic_profile.svg';
import PlayIcon from '../../assets/ic_play.svg';

const LIST_OPTIONS = [
    { value: 'NONE', label: 'Не выбрано' },
    { value: 'READING', label: 'Читаю' },
    { value: 'PLAN_TO_READ', label: 'Запланировано' },
    { value: 'COMPLETED', label: 'Прочитано' },
    { value: 'DROPPED', label: 'Брошено' },
    { value: 'ON_HOLD', label: 'На паузе' },
];

const VALID_LIST_STATUSES = new Set([
    'READING',
    'PLAN_TO_READ',
    'COMPLETED',
    'DROPPED',
    'ON_HOLD',
]);

const MangaDetailsPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const scrolled = useScrolled();
    const [manga, setManga] = useState(null);
    const [chapters, setChapters] = useState([]);
    // Starts true so the first render shows the skeleton, not a "not found" flash.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [coverUrl, setCoverUrl] = useState(null);
    const [listStatus, setListStatus] = useState('NONE');
    const [listMessage, setListMessage] = useState(null);
    const [teamProfile, setTeamProfile] = useState(null);
    const [teamLogoUrl, setTeamLogoUrl] = useState(null);
    const [chaptersError, setChaptersError] = useState(null);
    const [chaptersLoaded, setChaptersLoaded] = useState(false);
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);

    const isAllowed = MangaApiService.isAllowedDomain();
    const hasSession = !!localStorage.getItem('sessionKey');
    const isRoleActive = (value) => value === true || value === 'true' || value === 1 || value === '1';
    const isModerator = isRoleActive(localStorage.getItem('mod'));
    const isEditor = isRoleActive(localStorage.getItem('editor'));
    const isStaff = isModerator || isEditor;
    const mangaTeamId = manga?.teamId || manga?.team_id || manga?.team?.id;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const loadManga = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
        }
        setError(null);
        try {
            const response = await MangaApiService.getManga(slug);
            const payload = response?.data || response;
            const hasManga = payload && (payload.id || payload.slug || payload.title || payload.name);
            if (response?.type === 'success' || hasManga) {
                setManga(payload);
                document.title = `${payload?.title || payload?.name || 'Манга'} на animix`;
            } else {
                setError('Не удалось загрузить мангу.');
            }
        } catch (err) {
            setError(getMangaErrorMessage(err, 'Не удалось загрузить мангу.', {
                404: 'Тайтл не найден. Возможно, он скрыт настройкой 18+ или ещё на модерации.',
            }));
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        if (isAllowed) {
            loadManga();
        }
    }, [isAllowed, loadManga]);

    useEffect(() => {
        let isActive = true;
        const resolveCover = async () => {
            if (!manga) {
                if (isActive) {
                    setCoverUrl(null);
                    setImageLoaded(false);
                }
                return;
            }
            const direct = MangaApiService.resolveCoverUrl(manga);
            if (direct && direct !== coverUrl && isActive) {
                setImageLoaded(false);
                setCoverUrl(direct);
            }
            if (direct) {
                return;
            }
            try {
                const resolved = await MangaApiService.resolveCoverUrlAsync(manga);
                if (isActive && resolved !== coverUrl) {
                    setImageLoaded(false);
                    setCoverUrl(resolved || null);
                }
            } catch (error) {
                if (isActive && !direct && coverUrl !== null) {
                    setCoverUrl(null);
                }
            }
        };
        resolveCover();
        return () => {
            isActive = false;
        };
    }, [manga]);

    useEffect(() => {
        if (!coverUrl) {
            return undefined;
        }
        let isActive = true;
        const img = new Image();
        img.src = coverUrl;
        const finalize = () => {
            if (isActive) {
                setImageLoaded(true);
            }
        };
        if (img.complete) {
            finalize();
        } else {
            img.onload = finalize;
            img.onerror = finalize;
        }
        return () => {
            isActive = false;
        };
    }, [coverUrl]);

    useEffect(() => {
        if (!isAllowed) {
            return;
        }
        if (!mangaTeamId) {
            setTeamProfile(null);
            return;
        }
        let isActive = true;
        setTeamProfile(null);
        const loadTeam = async () => {
            try {
                const response = await MangaApiService.getTeam(mangaTeamId);
                const payload = response?.data || response;
                const teamData = payload?.data || payload;
                if (isActive) {
                    setTeamProfile(teamData);
                }
            } catch (err) {
                if (isActive) {
                    setTeamProfile(null);
                }
            }
        };
        loadTeam();
        return () => {
            isActive = false;
        };
    }, [mangaTeamId, isAllowed]);

    useEffect(() => {
        let isActive = true;
        const resolveTeamLogo = async () => {
            if (!teamProfile) {
                if (isActive) {
                    setTeamLogoUrl(null);
                }
                return;
            }
            const fileId = teamProfile.logoFileId || teamProfile.logo_file_id || teamProfile.logo?.fileId || teamProfile.logo?.id;
            const bucket = teamProfile.logoStorageBucket || teamProfile.logo_storage_bucket || teamProfile.logo?.bucket;
            if (!fileId || !bucket) {
                if (isActive) {
                    setTeamLogoUrl(null);
                }
                return;
            }
            const direct = MangaApiService.resolveFileUrl({
                fileId,
                bucket,
                fileUrl: teamProfile.logoFileUrl || teamProfile.logo_file_url || teamProfile.logo?.fileUrl || teamProfile.logo?.file_url,
                url: teamProfile.logoUrl || teamProfile.logo?.url,
            }, bucket);
            if (direct) {
                if (isActive) {
                    setTeamLogoUrl(direct);
                }
                return;
            }
            try {
                const resolved = await MangaApiService.resolveFileUrlAsync({ fileId, bucket }, bucket);
                if (isActive) {
                    setTeamLogoUrl(resolved);
                }
            } catch (error) {
                if (isActive) {
                    setTeamLogoUrl(null);
                }
            }
        };
        resolveTeamLogo();
        return () => {
            isActive = false;
        };
    }, [teamProfile]);

    useEffect(() => {
        if (!manga?.id) {
            return;
        }
        setChaptersLoaded(false);
        const loadChapters = async () => {
            setChaptersError(null);
            try {
                const response = await MangaApiService.getChapters(manga.id);
                setChapters(unwrapList(response));
            } catch (err) {
                setChaptersError(getMangaErrorMessage(err, 'Не удалось загрузить главы.'));
            } finally {
                setChaptersLoaded(true);
            }
        };
        loadChapters();
    }, [manga?.id]);

    useEffect(() => {
        if (!isAllowed || !hasSession || !manga?.id) {
            setListStatus('NONE');
            return;
        }
        let isActive = true;
        const loadListStatus = async () => {
            const findEntry = (list) => list.find((item) => {
                const itemManga = item?.manga || item?.mangaEntity || item?.mangaItem || item?.mangaData || item;
                const itemId = getMangaId(itemManga)
                    || item?.mangaId
                    || item?.manga_id
                    || item?.targetId;
                return itemId !== null && itemId !== undefined && String(itemId) === String(manga.id);
            });
            const resolveStatus = (entry) => {
                const rawStatus = entry?.status || entry?.listStatus || entry?.state || entry?.list_status;
                const normalized = rawStatus ? String(rawStatus).toUpperCase() : 'NONE';
                return VALID_LIST_STATUSES.has(normalized) ? normalized : 'NONE';
            };
            try {
                let list = [];
                try {
                    const response = await MangaApiService.getUserLists();
                    list = unwrapList(response);
                } catch (error) {
                    list = [];
                }
                let entry = findEntry(list);
                if (!entry) {
                    for (const status of VALID_LIST_STATUSES) {
                        const response = await MangaApiService.getUserLists(status);
                        const items = unwrapList(response);
                        entry = findEntry(items);
                        if (entry) {
                            break;
                        }
                    }
                }
                if (isActive) {
                    setListStatus(resolveStatus(entry));
                }
            } catch (err) {
                if (isActive) {
                    setListStatus('NONE');
                }
            }
        };
        loadListStatus();
        return () => {
            isActive = false;
        };
    }, [isAllowed, hasSession, manga?.id]);

    const sortedChapters = useMemo(() => sortChapters(chapters), [chapters]);
    const progress = useMemo(() => readMangaProgress(manga?.id), [manga?.id]);
    const progressChapter = useMemo(() => (progress
        ? sortedChapters.find((chapter) => String(getChapterId(chapter)) === String(progress.chapterId)) || null
        : null), [progress, sortedChapters]);

    const handleStatusChange = async (value) => {
        setListStatus(value);
        setListMessage(null);
        if (value === 'NONE' || !manga?.id) {
            return;
        }
        if (!VALID_LIST_STATUSES.has(value)) {
            setListMessage('Некорректный статус списка.');
            return;
        }
        try {
            await MangaApiService.updateUserList({ mangaId: manga.id, status: value });
            setListMessage('Список обновлён.');
        } catch (err) {
            setListMessage(getMangaErrorMessage(err, 'Не удалось обновить список.'));
        }
    };

    if (!isAllowed) {
        return (
            <PageShell>
                <MangaAccessNotice />
            </PageShell>
        );
    }

    if (loading) {
        return (
            <div>
                <Header scrolled={scrolled} />
                <DetailSkeleton />
            </div>
        );
    }

    if (!manga) {
        return (
            <PageShell>
                <div className="ui-empty">
                    <h1 className="ui-empty__title">Тайтл не найден</h1>
                    <p className="ui-empty__text">{error || 'Возможно, его удалили или ссылка устарела.'}</p>
                    <div className="page-header__actions">
                        <button type="button" className="ui-btn ui-btn--secondary" onClick={() => loadManga()}>Повторить</button>
                        <Link className="ui-btn ui-btn--secondary" to="/manga">В каталог манги</Link>
                    </div>
                </div>
            </PageShell>
        );
    }

    const title = manga.title || manga.name || manga.slug || 'Манга';
    const description = manga.description || manga.summary || '';
    const genresRaw = manga.genres || manga.tags || '';
    const genres = Array.isArray(genresRaw) ? genresRaw.join(', ') : genresRaw;
    const author = manga.author || manga.writer || manga.artist;
    const publisher = manga.publisher || manga.publisherName || manga.publisher_name;
    const status = manga.status || manga.state;
    const statusLabel = status ? translateStatus(status) : null;
    const isNsfw = manga.isNsfw ?? manga.nsfw ?? manga.is_nsfw;
    const isCompleted = manga.completed ?? manga.isCompleted ?? manga.is_completed;
    const teamId = mangaTeamId || teamProfile?.id;
    const teamName = teamProfile?.name
        || teamProfile?.title
        || manga.teamName
        || manga.team_name
        || manga.teamTitle
        || manga.team?.name
        || manga.team?.title;
    const teamLabel = teamName || (teamId ? `Команда #${teamId}` : null);
    const firstChapterId = sortedChapters.length > 0 ? getChapterId(sortedChapters[0]) : null;
    const readUrl = (chapterId) => `/manga/${slug}/read?chapterId=${chapterId}`;

    return (
        <div>
            <Header scrolled={scrolled} />

            <div className="anime-info-content manga-info-content ui-enter">
                {coverUrl ? (
                    <img
                        src={coverUrl}
                        alt={title}
                        className={`blud-back ${imageLoaded ? 'loaded' : ''}`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageLoaded(true)}
                    />
                ) : null}

                <div className="anime-info-container">
                    <MangaCardComponent manga={manga} />
                    <div className="anime-info-div">
                        <div className="center-content manga-tags">
                            {isNsfw ? <span className="manga-tag manga-tag-nsfw">NSFW</span> : null}
                            {isCompleted ? <span className="manga-tag">Перевод завершён</span> : null}
                            {statusLabel && isStaff ? (
                                <span className="manga-tag">{statusLabel}</span>
                            ) : null}
                            {author ? <span className="manga-tag">{author}</span> : null}
                        </div>
                        <h1 className="anime-name">{title}</h1>
                        {teamLabel ? (
                            <div className="manga-team-preview">
                                <div className="manga-team-preview-logo">
                                    <img src={teamLogoUrl || teamIcon} alt={teamLabel} />
                                </div>
                                <div className="manga-team-preview-body">
                                    <div className="manga-team-preview-label">Команда</div>
                                    {teamId ? (
                                        <Link className="manga-team-link" to={`/manga/teams/${teamId}`}>
                                            {teamLabel}
                                        </Link>
                                    ) : (
                                        <span className="manga-team-preview-name">{teamLabel}</span>
                                    )}
                                </div>
                            </div>
                        ) : null}
                        <p className="anime-text">
                            {genres ? `Жанры: ${genres}` : 'Жанры не указаны'}
                        </p>
                        {publisher ? (
                            <p className="anime-text">Издатель: {publisher}</p>
                        ) : null}

                        <div className="anime-actions-con">
                            {hasSession && (progressChapter || firstChapterId) ? (
                                <button
                                    className="actionButton playButton2 manga-play-button"
                                    onClick={() => navigate(readUrl(getChapterId(progressChapter || sortedChapters[0])))}
                                >
                                    <img src={PlayIcon} alt="" className="playIcon" />
                                    {progressChapter ? `Продолжить: ${formatChapterLabel(progressChapter)}` : 'Читать'}
                                </button>
                            ) : null}
                            {hasSession ? (
                                <>
                                    <CustomDropdown
                                        placeholder="Не выбрано"
                                        value={listStatus}
                                        onChange={handleStatusChange}
                                        options={LIST_OPTIONS}
                                    />
                                    {listMessage ? <span className="manga-inline-note">{listMessage}</span> : null}
                                </>
                            ) : (
                                <span className="manga-inline-note">Войдите, чтобы читать мангу и вести списки</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className={`anime-half-con ui-enter${hasSession ? '' : ' catalog'}`}>
                <div className="anime-half-desc">
                    <h1>О чём</h1>
                    <div className="anime-text-small-container">
                        <p className={`anime-text-small-desc ${descriptionExpanded ? 'expanded' : ''}`}>
                            {description || 'Описание пока отсутствует.'}
                        </p>
                        {!descriptionExpanded && description.length > 300 ? (
                            <button className="expand-button" onClick={() => setDescriptionExpanded(true)}>
                                Показать полностью
                            </button>
                        ) : null}
                    </div>
                    <MangaReviews
                        manga={manga}
                        hasSession={hasSession}
                        isModerator={isModerator}
                        onRatingChange={() => loadManga({ silent: true })}
                    />
                </div>

                {hasSession ? (
                    <div className="anime-half">
                        <h1>Главы</h1>
                        {chaptersError ? (
                            <div className="manga-inline-warning">{chaptersError}</div>
                        ) : !chaptersLoaded ? (
                            <div className="loader" />
                        ) : (
                            <MangaChapterNavigator
                                chapters={sortedChapters}
                                slug={slug}
                                currentChapterId={progressChapter ? getChapterId(progressChapter) : null}
                                emptyText="Глав пока нет."
                            />
                        )}
                    </div>
                ) : null}
            </div>

            <Footer />
        </div>
    );
};

export default MangaDetailsPage;
